// Aggregated telemetry for the query permitting.
//
// Each metric accumulates in memory via its own record* methods and
// serializes to zero or more track events on drain,resetting its counters.

import {
  PoolStatsTrackEvent,
  QUERY_PERMIT_POOL_STATS_EVENT,
  QUERY_PERMIT_SOFT_THROTTLE_EVENT,
  QueryPermitClientType,
  QueryPermitTrackEvent,
  SoftThrottleTrackEvent,
} from './tracking';

const MS_PER_MINUTE = 60_000;

// Backoff levels at or above this collapse into the top bucket
const SOFT_THROTTLE_TOP_LEVEL = 5;

export interface QueryPermitMetric {
  // Serialize accumulated state into zero or more track events and reset.
  drain(nowMs: number): QueryPermitTrackEvent[];
}

interface SoftThrottleAccumulator {
  // nowMs when the window's first sample landed — the denominator anchor for sustained_rpm
  startTime: number;
  // Total number of queries
  sampleCount: number;
  // Number of queries that were throttled
  engagedCount: number;
  // bucketLevel -> count of queries at that backoff depth
  levelCounts: Map<number, number>;
}

export class SoftThrottleMetric implements QueryPermitMetric {
  private readonly perProject = new Map<number, SoftThrottleAccumulator>();

  constructor(private readonly clientType: QueryPermitClientType) {}

  // Record one soft-throttle-evaluated grant
  record(projectId: number, backoffLevel: number, nowMs: number): void {
    let acc = this.perProject.get(projectId);
    if (!acc) {
      acc = {startTime: nowMs, sampleCount: 0, engagedCount: 0, levelCounts: new Map()};
      this.perProject.set(projectId, acc);
    }
    acc.sampleCount++;
    if (backoffLevel > 0) {
      acc.engagedCount++;
      const bucket = Math.min(backoffLevel, SOFT_THROTTLE_TOP_LEVEL);
      acc.levelCounts.set(bucket, (acc.levelCounts.get(bucket) ?? 0) + 1);
    }
  }

  drain(nowMs: number): SoftThrottleTrackEvent[] {
    const events: SoftThrottleTrackEvent[] = [];
    for (const [projectId, acc] of this.perProject) {
      // Only windows that engaged the throttle are worth reporting.
      if (acc.engagedCount === 0) {
        continue;
      }
      const elapsedMs = nowMs - acc.startTime;
      events.push({
        name: QUERY_PERMIT_SOFT_THROTTLE_EVENT,
        properties: {
          project_id: projectId,
          client_type: this.clientType,
          engaged_count: acc.engagedCount,
          backoff_level_1: acc.levelCounts.get(1) ?? 0,
          backoff_level_2: acc.levelCounts.get(2) ?? 0,
          backoff_level_3: acc.levelCounts.get(3) ?? 0,
          backoff_level_4: acc.levelCounts.get(4) ?? 0,
          backoff_level_5_plus: acc.levelCounts.get(SOFT_THROTTLE_TOP_LEVEL) ?? 0,
          sustained_rpm: elapsedMs > 0 ? (acc.sampleCount / elapsedMs) * MS_PER_MINUTE : 0,
        },
      });
    }
    this.perProject.clear();
    return events;
  }
}

// One sample of a project's pool state, taken wherever the pool's slot/queue counts change.
export interface PoolSample {
  // Waiting-queue depth (getPendingCount)
  pending: number;
  // In-flight permits held (getCount)
  inFlight: number;
  // Current cap (getMaxCount)
  cap: number;
}

interface PoolStatsAccumulator {
  grantCount: number;
  backgroundPoolGrantCount: number;
  backgroundTabGrantCount: number;

  // Queue / saturation
  sampleCount: number;
  pendingPeak: number;
  saturatedSamples: number;
  // nowMs when the queue last went from empty to non-empty, or null when the queue is empty.
  // Persists across drains so a busy period spanning a flush boundary is timed once.
  busyStart: number | null;

  // Wait
  waitForGrantMsMax: number;
  waitForGrantMsTotal: number;

  // Reclaim / leak
  reclaimedTotal: number;

  // Drain
  drainCount: number;
  drainMsMax: number;
  drainMsTotal: number;
}

function emptyPoolStats(): PoolStatsAccumulator {
  return {
    grantCount: 0,
    backgroundPoolGrantCount: 0,
    backgroundTabGrantCount: 0,
    sampleCount: 0,
    pendingPeak: 0,
    saturatedSamples: 0,
    busyStart: null,
    waitForGrantMsMax: 0,
    waitForGrantMsTotal: 0,
    reclaimedTotal: 0,
    drainCount: 0,
    drainMsMax: 0,
    drainMsTotal: 0,
  };
}

// Per-project aggregate of the semaphore's load behavior — throughput, queue/saturation, wait,
// involuntary reclaim, and busy-period drain — flushed as one query_permit.pool_stats event per
// active project each window. Soft throttle is tracked separately (SoftThrottleMetric).
export class PoolStatsMetric implements QueryPermitMetric {
  private readonly perProject = new Map<number, PoolStatsAccumulator>();

  constructor(private readonly clientType: QueryPermitClientType) {}

  private get(projectId: number): PoolStatsAccumulator {
    let acc = this.perProject.get(projectId);
    if (!acc) {
      acc = emptyPoolStats();
      this.perProject.set(projectId, acc);
    }
    return acc;
  }

  // Record one grant, the wait-for-grant that preceded it (ms), and the foreground/background tiers
  // it was served at. The wait is time queued for a concurrency slot only; soft-throttle backoff
  // (applied after the grant, owned by SoftThrottleMetric) is excluded, so it isolates
  // concurrency-limiting latency. `tiers` flags let a grant count as background on either the pool
  // axis (prefetch) or the tab axis (non-viewed tab), independently.
  recordGrant(
    projectId: number,
    waitForGrantMs: number,
    tiers: {backgroundPool: boolean; backgroundTab: boolean},
  ): void {
    const acc = this.get(projectId);
    acc.grantCount++;
    if (tiers.backgroundPool) {
      acc.backgroundPoolGrantCount++;
    }
    if (tiers.backgroundTab) {
      acc.backgroundTabGrantCount++;
    }
    acc.waitForGrantMsMax = Math.max(acc.waitForGrantMsMax, waitForGrantMs);
    acc.waitForGrantMsTotal += waitForGrantMs;
  }

  // Record one pool-state sample. Call wherever pending/in-flight can change.
  recordSample(projectId: number, sample: PoolSample, nowMs: number): void {
    const acc = this.get(projectId);
    acc.sampleCount++;
    acc.pendingPeak = Math.max(acc.pendingPeak, sample.pending);
    // Count samples that caught the pool completely full; saturated_samples / sample_count
    // approximates the fraction of the window the cap was the binding constraint.
    if (sample.inFlight >= sample.cap) {
      acc.saturatedSamples++;
    }
    // Busy-period edge detection: the queue is "busy" while it has waiters.
    if (acc.busyStart === null && sample.pending > 0) {
      acc.busyStart = nowMs;
    } else if (acc.busyStart !== null && sample.pending === 0) {
      const drainMs = nowMs - acc.busyStart;
      acc.drainCount++;
      acc.drainMsMax = Math.max(acc.drainMsMax, drainMs);
      acc.drainMsTotal += drainMs;
      acc.busyStart = null;
    }
  }

  // Record one involuntary reclaim (max-hold or port-dropped). Not called for pre-grant cancels,
  // which are normal rather than leaks.
  recordReclaim(projectId: number): void {
    this.get(projectId).reclaimedTotal++;
  }

  drain(_nowMs: number): PoolStatsTrackEvent[] {
    const events: PoolStatsTrackEvent[] = [];
    for (const [projectId, acc] of this.perProject) {
      // Only windows where something happened are worth reporting. drainCount gates too because
      // a busy period's completion may land in a window whose own peaks are otherwise zero.
      const active =
        acc.grantCount > 0 ||
        acc.pendingPeak > 0 ||
        acc.saturatedSamples > 0 ||
        acc.reclaimedTotal > 0 ||
        acc.drainCount > 0;
      if (active) {
        events.push({
          name: QUERY_PERMIT_POOL_STATS_EVENT,
          properties: {
            project_id: projectId,
            client_type: this.clientType,
            grant_count: acc.grantCount,
            background_pool_grant_count: acc.backgroundPoolGrantCount,
            background_tab_grant_count: acc.backgroundTabGrantCount,
            sample_count: acc.sampleCount,
            pending_peak: acc.pendingPeak,
            saturated_samples: acc.saturatedSamples,
            wait_for_grant_ms_max: acc.waitForGrantMsMax,
            wait_for_grant_ms_total: acc.waitForGrantMsTotal,
            reclaimed_total: acc.reclaimedTotal,
            drain_count: acc.drainCount,
            drain_ms_max: acc.drainMsMax,
            drain_ms_total: acc.drainMsTotal,
          },
        });
      }
      // Reset the completed-window accumulators but keep an open busy period (busyStart) so a
      // busy period that spans this flush boundary is timed from its true start.
      if (acc.busyStart === null) {
        this.perProject.delete(projectId);
      } else {
        this.perProject.set(projectId, {...emptyPoolStats(), busyStart: acc.busyStart});
      }
    }
    return events;
  }
}
